import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@velocity/ui';
import { isValidPhoneNumber } from 'libphonenumber-js';
import ls from 'localstorage-slim';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import {
  LegalConditionsMarketing,
  PrivateCustomer,
} from '../../../../types/instant-lease-api';
import { getPartnerText } from '../../../../utils/getter';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import {
  FormCheckboxField,
  FormPhoneField,
  FormSection,
  FormTextField,
} from '../../../common/velocity-form';

const PHONE_ERROR_MESSAGE =
  'Bitte geben Sie eine gültige Handynummer an, z.B.: +49 40 471040';

// Consent copy text (Figma: body/text-tight-100)
const consentTextClass = 'text-tight-100';

interface ContactDataProps {
  onSubmit: (owner: ContactTemplateData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
  marketingAgreements?: LegalConditionsMarketing;
  singer?: boolean;
  hasCoApplicant?: boolean;
}

export interface ContactTemplateData {
  email: string;
  phone_number: string;
  marketing_email?: boolean | undefined;
  marketing_phone?: boolean | undefined;
}

const ContactTemplate: React.FC<ContactDataProps> = ({
  privateCustomer,
  tabButton,
  onSubmit,
  loading,
  marketingAgreements,
  singer,
  hasCoApplicant,
}) => {
  // Auth Storage
  const { email } = useSelector((state: RootState) => state.auth);

  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // check whether is it posbank loyalty prometion
  const isPostBank =
    activeApplication?.loyalty_card?.loyalty_card_type?.toLowerCase() ===
    'postbank';

  // validationSchema
  const validationSchema = yup.object().shape({
    phone_number: yup
      .string()
      .required('Pflichtfeld')
      .test(
        'is-valid-phone',
        PHONE_ERROR_MESSAGE,
        (value) => !!value && isValidPhoneNumber(value),
      ),
    email: yup
      .string()
      .email('Bitte vollständige Email Adresse eintragen')
      .required('Pflichtfeld')
      .test(
        'main-signer-email-different',
        'Bitte verwende eine andere E-Mail-Adresse als der Antragsteller.',
        (value) => {
          if (!hasCoApplicant) {
            return true;
          }
          const storedEmail = ls.get('email') as string | null | undefined;
          if (!value || !storedEmail) {
            return true;
          }
          return (
            value.trim().toLowerCase() !== storedEmail.trim().toLowerCase()
          );
        },
      ),
    marketing_email: yup.boolean().optional(),
    marketing_phone: yup.boolean().optional(),
  });

  const contacts = privateCustomer?.person?.contact_details;
  // Stored as full international number, e.g. "+4940471040"
  const contactPhoneNumber = (
    contacts?.phone_numbers?.[0]?.value || ''
  ).replace(/\s+/g, '');

  const getEmail = () => {
    if (contacts?.emails && contacts?.emails?.length > 0) {
      return contacts?.emails[0].value;
    } else if (!singer) {
      return email;
    }
  };

  // Form hook
  const {
    register,
    control,
    trigger,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm<ContactTemplateData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as Resolver<ContactTemplateData>,
    defaultValues: {
      phone_number: contactPhoneNumber,
      email: getEmail(),
      marketing_phone: marketingAgreements?.phone_consent_agreed?.answer,
      marketing_email: marketingAgreements?.email_consent_agreed?.answer,
    },
  });

  useEffect(() => {
    if (!_.isEmpty(contacts) && contacts?.phone_numbers?.length) {
      setValue('phone_number', contactPhoneNumber, {
        shouldValidate: contactPhoneNumber === '',
      });
      trigger();
    }
  }, [contactPhoneNumber, contacts, setValue, trigger]);

  // Submit Data
  const submitData = (data: ContactTemplateData) => {
    onSubmit({
      phone_number: data.phone_number.replace(/\s+/g, ''),
      email: data.email,
      marketing_email: data.marketing_email,
      marketing_phone: data.marketing_phone,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitData)}>
      {/* Figma: card padding 20px (desktop) / 16px (tablet, mobile) */}
      <FormSection title='Kontaktdaten' className='px-4 lg:px-5'>
        <FormTextField
          label='E-Mail'
          type='email'
          required
          autoComplete='email'
          placeholder='Deine E-Mail-Adresse'
          disabled={!hasCoApplicant}
          defaultValue={getValues('email')}
          {...register('email')}
          errorMessage={errors.email?.message}
        />

        <Controller
          name='phone_number'
          control={control}
          render={({ field, fieldState }) => (
            <FormPhoneField
              label='Mobilnummer'
              required
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        {!isPostBank && (
          <>
            <div className='flex flex-col gap-1'>
              <p className={consentTextClass}>
                Ich willige ein, dass Ayvens mich zwecks Information über
                Produkte (Leasingangebote, allgemeine oder vertragsbezogene
                Werbung) und Dienstleistungen (z.B. Vertragsverlängerungen,
                zusätzliche Dienste), Einladung zu Events/Panels und zur Markt-
                und Meinungsforschung kontaktiert, und zwar per:
              </p>

              <div className='flex flex-col gap-4'>
                <Controller
                  name='marketing_email'
                  control={control}
                  render={({ field }) => (
                    <FormCheckboxField
                      name={field.name}
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      label={
                        <span className={consentTextClass}>
                          (optional) E-Mail (Name, Vorname, E-Mail-Adresse)
                        </span>
                      }
                    />
                  )}
                />
                <Controller
                  name='marketing_phone'
                  control={control}
                  render={({ field }) => (
                    <FormCheckboxField
                      name={field.name}
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      label={
                        <span className={consentTextClass}>
                          (optional) Telefon, SMS, Messenger (Name, Vorname,
                          Telefonnummer)
                        </span>
                      }
                    />
                  )}
                />
              </div>
            </div>

            <p className={consentTextClass}>
              Meine Einwilligung umfasst auch eine Analyse von Öffnungs- und
              Klickraten sowie deren Speicherung und Auswertung in
              Empfängerprofilen zu Zwecken der nutzeroptimierten Gestaltung
              künftiger Mitteilungen. Hinweis: Meine Einwilligung kann ich
              jederzeit mit Wirkung für die Zukunft ganz oder teilweise
              widerrufen.
              <br />
              Zum Beispiel per E-Mail an{' '}
              <a
                href={`mailto:${getPartnerText('terms_email')}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {getPartnerText('terms_email')}
              </a>
            </p>
          </>
        )}
      </FormSection>

      <>
        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next'
                type='back-next'
                data-testid='disabled-forward-button'
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading,
                  onClick: handleSubmit(submitData),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  variant='secondary-outlined'
                  size='m'
                  loading={loading}
                  onClick={handleSubmit(submitData)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading,
              dataTestId: 'start-fetching',
              children: 'nächster Schritt',
            }}
          />
        )}
      </>
    </form>
  );
};

export default ContactTemplate;
